import { z } from 'zod';
import { ACCOUNT_TYPES, type AccountType } from './accounts';
import { utf8 } from './encoding';
import {
  VaultKey,
  sealedBlobSchema,
  unlockWithPassword,
  unlockWithRecoveryCode,
  vaultKeyringSchema,
  type VaultKeyring,
} from './vault';

export const BACKUP_FORMAT = 'curentia-backup';

export const backupFileSchema = z.strictObject({
  format: z.literal(BACKUP_FORMAT),
  v: z.literal(1),
  createdAt: z.iso.datetime(),
  account: z.strictObject({ id: z.string().regex(/^[a-f0-9]{24}$/), type: z.enum(ACCOUNT_TYPES) }),
  keyring: vaultKeyringSchema,
  payload: sealedBlobSchema,
});

/**
 * An encrypted backup file. Everything but the account ID and type is
 * encrypted: the contents under the vault key, and the vault key under the
 * password and the recovery code. The payload's additional data binds every
 * other field, so none can be swapped without breaking decryption.
 */
export type BackupFile = z.infer<typeof backupFileSchema>;

function payloadContext(file: Omit<BackupFile, 'payload'>): string {
  const ordered = {
    format: file.format,
    v: file.v,
    createdAt: file.createdAt,
    account: { id: file.account.id, type: file.account.type },
    keyring: {
      v: file.keyring.v,
      kdf: file.keyring.kdf,
      salt: file.keyring.salt,
      password: { iv: file.keyring.password.iv, data: file.keyring.password.data },
      recovery: { iv: file.keyring.recovery.iv, data: file.keyring.recovery.data },
    },
  };
  return `backup\0${JSON.stringify(ordered)}`;
}

/** Builds a backup of the given contents, which the app chooses: device keys, records, settings. */
export async function createBackup(
  vaultKey: VaultKey,
  keyring: VaultKeyring,
  account: { id: string; type: AccountType },
  contents: unknown,
  createdAt = new Date().toISOString(),
): Promise<BackupFile> {
  const head = { format: BACKUP_FORMAT, v: 1, createdAt, account, keyring } as const;
  const payload = await vaultKey.encrypt(utf8(JSON.stringify(contents)), payloadContext(head));
  return { ...head, payload };
}

/** Reads a backup file's text. Throws when it is not a Curentia backup. */
export function parseBackupFile(text: string): BackupFile {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    throw new Error('This file is not a Curentia backup');
  }
  const parsed = backupFileSchema.safeParse(value);
  if (!parsed.success) throw new Error('This file is not a Curentia backup');
  return parsed.data;
}

/** Decrypts a backup's contents with a vault key already unlocked. */
export async function readBackup<T = unknown>(file: BackupFile, vaultKey: VaultKey): Promise<T> {
  const { payload, ...head } = file;
  return JSON.parse(new TextDecoder().decode(await vaultKey.decrypt(payload, payloadContext(head)))) as T;
}

/**
 * Opens a backup with the password or the recovery code. Throws
 * WrongSecretError when the secret is wrong. With the password, the login key
 * comes back too.
 */
export async function restoreBackup<T = unknown>(
  file: BackupFile,
  secret: { password: string } | { recoveryCode: string },
): Promise<{ vaultKey: VaultKey; contents: T; loginKey?: string }> {
  if ('password' in secret) {
    const { vaultKey, loginKey } = await unlockWithPassword(file.keyring, secret.password);
    return { vaultKey, loginKey, contents: await readBackup<T>(file, vaultKey) };
  }
  const vaultKey = await unlockWithRecoveryCode(file.keyring, secret.recoveryCode);
  return { vaultKey, contents: await readBackup<T>(file, vaultKey) };
}
